import { getApiBase } from './getApiBase';

export class ApiError extends Error {
  readonly status: number;

  readonly body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.location !== 'undefined';
}

function isLocalhostApi(url: URL): boolean {
  return url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '::1';
}

async function fetchJson(url: string, init: RequestInit): Promise<Response> {
  return fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers || {})
    }
  });
}

export async function apiJson<T>(
  path: string,
  init?: RequestInit & { query?: Record<string, string | number | boolean | undefined | null> }
): Promise<T> {
  const base = getApiBase();
  const full =
    path.startsWith('http://') || path.startsWith('https://')
      ? path
      : `${base}/${path.replace(/^\//, '')}`;
  const url = new URL(full);
  if (init?.query) {
    Object.entries(init.query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }
  const { query: _q, ...rest } = init || {};
  let res: Response;
  try {
    res = await fetchJson(url.toString(), rest);
  } catch (err) {
    const fallbackBase = isBrowser() ? `${window.location.origin}/api` : null;
    const shouldRetry = fallbackBase && isLocalhostApi(url) && !url.toString().startsWith(fallbackBase);
    if (shouldRetry) {
      const fallbackUrl = new URL(url.toString());
      fallbackUrl.protocol = window.location.protocol;
      fallbackUrl.host = window.location.host;
      try {
        res = await fetchJson(fallbackUrl.toString(), rest);
      } catch {
        throw new ApiError(
          `Unable to reach the API at ${url.origin}. Check that the backend is running and that REACT_APP_API_BASE or window.VIP_MP_API_BASE points to the correct host.`,
          0,
          err
        );
      }
    } else {
      throw new ApiError(
        `Unable to reach the API at ${url.origin}. Check that the backend is running and that REACT_APP_API_BASE or window.VIP_MP_API_BASE points to the correct host.`,
        0,
        err
      );
    }
  }
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    if (res.status === 401 && isBrowser()) {
      window.dispatchEvent(new CustomEvent('vip-mp:session-expired'));
    }
    const msg =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : res.statusText;
    throw new ApiError(msg || 'Request failed', res.status, data);
  }
  return data as T;
}
